import { Injectable } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { ePartyType } from '../../enums';
import { Party } from '../../interfaces/parties/party';
import { Utilities } from '../../utilities';
import { share } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PathConversionService {

  constructor() { }

  public convertPath(order: Order, path: string, showSinglePartyPath = false, entityId: string | undefined = undefined): string {
    let formattedPath: string;
    if (path.startsWith('Parties')) {
      formattedPath = this.convertPartyPath(order, path);
    } else if (path.startsWith('Assets')) {
      var assetID = this.parseBaseEntityID(path);
      const assets = order.assets?.sort((a, b) => a.priority - b.priority);
      const asset = assets?.find(a => a.id === assetID);

      if (asset) {
        const assetIndex = assets!.indexOf(asset)!;
        formattedPath = `Asset ${assetIndex + 1}`;
      } else {
        return 'Assets';
      }
    } else if (showSinglePartyPath === true) {
      return this.getLastPathItem(path);
    } else if (path.toLowerCase().startsWith('ultimatebeneficialownershipstructure') || path.toLowerCase().includes('shareholders')) {
      return this.convertShareholderPartyPath(order, path, entityId);
    } else {
      return '';
    }

    return formattedPath.replace(/\s+/g, ' ');
  }

  protected getLastPathItem(path: string): string {
    var splitPath = path.split('/');
    var item = splitPath.at(splitPath.length - 1);

    if (item) {
      return Utilities.spacePascalCase(item).trim();
    } else {
      return path;
    }
  }

  protected convertShareholderPartyPath(order: Order, path: string, entityId: string | undefined = undefined): string {
    var rows = order.ultimateBeneficialOwnershipRequest?.shareholders;
    let shareHolderNum: number | undefined = undefined;
    if (rows) {
      rows.sort((a, b) => a.party.priority - b.party.priority);

      // check if entity matches the shareholder party, contact details or address
      shareHolderNum = rows.findIndex(r => r.id === entityId);
      if (shareHolderNum == -1) {
        shareHolderNum = rows.findIndex(r => r.party.id === entityId);
      }
      if (shareHolderNum == -1) {
        shareHolderNum = rows.findIndex(r => r.party.contactDetails?.id === entityId);
      }
      if (shareHolderNum == -1) {
        shareHolderNum = rows.findIndex(r => r.party.contactDetails?.address.id === entityId);
      }
      if (shareHolderNum == -1) {
        shareHolderNum = rows.findIndex(r => r.attestorParty?.id === entityId);
      }
      if (shareHolderNum == -1) {
        shareHolderNum = rows.findIndex(r => r.attestorParty?.contactDetails?.id === entityId);
      }
    }

    if (shareHolderNum != undefined && shareHolderNum >= 0) {
      if (path.includes("AttestorParty")) {
        return `Shareholder ${shareHolderNum + 1} Attestor`;
      }
      else {
        return `Shareholder ${shareHolderNum + 1}`;
      }
    }
      
    return "";
  }

  protected convertPartyPath(order: Order, path: string): string {
    var partyID = this.parseBaseEntityID(path);

    if (!partyID) {
      return path;
    }

    const party = order.parties?.find(p => p.id === partyID);

    if (!party) {
      return path;
    }

    if (Utilities.isAssignor(party)) {
      return this.buildPartyString(p => Utilities.isAssignor(p), order, path, party, 'Assignor');
    } else if (Utilities.isAssignee(party)) {
      return this.buildPartyString(p => Utilities.isAssignee(p), order, path, party, 'Assignee');
    } else if (Utilities.isDebtor(party)) {
      return this.buildPartyString(p => Utilities.isDebtor(p), order, path, party, 'Debtor');
    } else if (Utilities.isDealer(party)) {
      return this.buildPartyString(p => Utilities.isDealer(p), order, path, party, 'Dealer');
    } else if (Utilities.isSecuredParty(party)) {
      return this.buildPartyString(p => Utilities.isSecuredParty(p), order, path, party, 'Secured Party');
    } else if (Utilities.isRegisteringAgent(party)) {
      return this.buildPartyString(p => Utilities.isRegisteringAgent(p), order, path, party, 'Registering Agent');
    } else if (Utilities.isAttestor(party)) {
      return 'Attestor';
    } else if (Utilities.isSigningAuthority(party)) {
      return this.buildPartyString(p => Utilities.isSigningAuthority(p), order, path, party, 'Signing Authority');
    } else {
      return path;
    }
  }

  private parseBaseEntityID(path: string): string {
    var splitPath = path.split('/');

    if (splitPath.length < 2) {
      return '';
    }

    return splitPath[1];
  }

  private buildPartyString(checker: (input: Party) => boolean, order: Order, path: string, party: Party, type: string): string {
    var parties = order.parties?.filter(p => checker(p)).sort((a, b) => a.priority - b.priority);

    if (!parties) {
      return type;
    }

    const index = parties.indexOf(party);

    if (index === -1) {
      return type;
    }

    return `${type} ${index + 1}`;
  }
}
